/**
 * Server-based quotations API functions.
 */

import { authenticatedHttpClient } from './httpClient';
import { Order } from '../models/order';
import {
  Quote,
  QuoteCreate,
  QuoteUpdate,
  QuoteQueryResult,
} from '../models/quote';

export const createQuoteServer = async (
  organizationId: string,
  quoteData: QuoteCreate
): Promise<Quote> => {
  try {
    return await authenticatedHttpClient.post<Quote>(
      `/quotes/${organizationId}`,
      { quote: quoteData }
    );
  } catch (error: any) {
    console.error('Error creating quote:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al crear la cotización'
    );
  }
};

export const getQuoteServer = async (
  organizationId: string,
  quoteId: string
): Promise<Quote> => {
  try {
    return await authenticatedHttpClient.get<Quote>(
      `/quotes/${organizationId}/${quoteId}`
    );
  } catch (error: any) {
    console.error('Error getting quote:', error);
    if (error.response?.status === 404) {
      throw new Error('Cotización no encontrada');
    }
    throw new Error(
      error.response?.data?.detail || 'Error al obtener la cotización'
    );
  }
};

export const getQuotesServer = async (
  organizationId: string,
  filters?: {
    clientId?: string;
    clientName?: string;
    creatorSalesPerson?: string;
    searchTerm?: string;
    minTotal?: number;
    maxTotal?: number;
    dateFrom?: string;
    dateTo?: string;
    deletedOnly?: boolean;
    limit?: number;
    offset?: number;
  }
): Promise<QuoteQueryResult> => {
  try {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          // Map camelCase filter keys to snake_case query params used by the API
          const paramKey =
            key === 'searchTerm'
              ? 'search_term'
              : key === 'clientId'
                ? 'client_id'
                : key === 'clientName'
                  ? 'client_name'
                  : key === 'creatorSalesPerson'
                    ? 'creator_sales_person'
                    : key === 'minTotal'
                      ? 'min_total'
                      : key === 'maxTotal'
                        ? 'max_total'
                        : key === 'dateFrom'
                          ? 'date_from'
                          : key === 'dateTo'
                            ? 'date_to'
                            : key === 'deletedOnly'
                              ? 'deleted_only'
                              : key;
          params.append(paramKey, String(value));
        }
      });
    }
    return await authenticatedHttpClient.get<QuoteQueryResult>(
      `/quotes/${organizationId}?${params.toString()}`
    );
  } catch (error: any) {
    console.error('Error getting quotes:', error);
    const detail = error.response?.data?.detail;
    const message =
      typeof detail === 'string'
        ? detail
        : detail?.message ||
          error.response?.data?.title ||
          'Error al obtener las cotizaciones';
    throw new Error(message);
  }
};

export const updateQuoteServer = async (
  organizationId: string,
  quoteId: string,
  quoteData: QuoteUpdate
): Promise<Quote> => {
  try {
    return await authenticatedHttpClient.put<Quote>(
      `/quotes/${organizationId}/${quoteId}`,
      quoteData
    );
  } catch (error: any) {
    console.error('Error updating quote:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al actualizar la cotización'
    );
  }
};

export const deleteQuoteServer = async (
  organizationId: string,
  quoteId: string,
  softDelete: boolean = true
): Promise<void> => {
  try {
    await authenticatedHttpClient.delete(
      `/quotes/${organizationId}/${quoteId}?soft_delete=${softDelete}`
    );
  } catch (error: any) {
    console.error('Error deleting quote:', error);
    throw new Error(
      error.response?.data?.detail || 'Error al eliminar la cotización'
    );
  }
};

export const convertQuoteServer = async (
  organizationId: string,
  quoteId: string
): Promise<Order> => {
  try {
    return await authenticatedHttpClient.post<Order>(
      `/quotes/${organizationId}/${quoteId}/convert`,
      {}
    );
  } catch (error: any) {
    console.error('Error converting quote:', error);
    throw new Error(
      error.response?.data?.detail ||
        'Error al convertir la cotización en venta'
    );
  }
};
